import deLocale from '@uploadcare/file-uploader/locales/file-uploader/de.js';
import enLocale from '@uploadcare/file-uploader/locales/file-uploader/en.js';
import type { OutputCollectionState, OutputFileEntry, UploadCtxProvider } from '@uploadcare/react-uploader';
import { defineLocale, FileUploaderRegular } from '@uploadcare/react-uploader';
import '@uploadcare/react-uploader/core.css';
import { PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { UPLOADCARE_CDN_NAME, UPLOADCARE_PUBLIC_KEY } from '../../../lib/constants.ts';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { UploadedFile } from '../../../server/dto/crm-attachment-dto.ts';
import { api } from '../../../utils/api.ts';
import { FileCard } from '../../crm-attachment/components/file-card.tsx';

defineLocale('de', deLocale);
defineLocale('en', enLocale);

type TaskFileUploaderProps = {
    value: UploadedFile[];
    onChange: (files: UploadedFile[]) => void;
};

export const TaskFileUploader = ({ value, onChange }: TaskFileUploaderProps) => {
    const { t, locale } = useLocale('tasks');
    const { tenantId, platoUser } = useAppState();
    const { data: uploadSignature, isLoading } = api.platoUser.query.uploadcare.getUploadSignature.useQuery();
    const uploaderRef = useRef<UploadCtxProvider | null>(null);

    const uploadMetadata = useMemo(
        () => ({
            tenantId: tenantId ?? '',
            userId: platoUser?.id ?? '',
        }),
        [tenantId, platoUser?.id],
    );

    const handleDoneClick = useCallback(
        (event: OutputCollectionState) => {
            const newFiles: UploadedFile[] = event.successEntries.map(
                (entry: OutputFileEntry<'success'>) => ({
                    externalUuid: entry.uuid,
                    cdnUrl: entry.cdnUrl,
                    cdnUrlModifiers: entry.cdnUrlModifiers,
                    name: entry.name,
                    size: entry.size,
                    mimeType: entry.mimeType,
                    isImage: entry.isImage,
                    s3Url: entry.fileInfo.s3Url ?? null,
                    s3Bucket: entry.fileInfo.s3Bucket ?? null,
                }),
            );

            onChange(newFiles);
        },
        [onChange],
    );

    const handleRemove = useCallback(
        (externalUuid: string) => {
            const uploaderApi = uploaderRef.current?.getAPI();
            const collectionState = uploaderApi?.getOutputCollectionState();
            const file = collectionState?.allEntries.find((entry) => entry.uuid === externalUuid);
            if (file) {
                uploaderApi?.removeFileByInternalId(file.internalId);
            }

            onChange(value.filter((file) => file.externalUuid !== externalUuid));
        },
        [value, onChange],
    );

    const apiRefCallback = useCallback(
        (instance: UploadCtxProvider | null) => {
            uploaderRef.current = instance;

            if (!instance) {
                return;
            }

            const uploaderApi = instance.getAPI();
            const collecitonState = uploaderApi.getOutputCollectionState();

            for (const file of value) {
                const isAlreadyAdded = collecitonState.allEntries.some(
                    (entry) => entry.uuid === file.externalUuid,
                );

                if (!isAlreadyAdded) {
                    uploaderApi.addFileFromCdnUrl(file.cdnUrl, {
                        silent: true,
                        source: 'api',
                    });
                }
            }
        },
        [value],
    );

    const handleAddFiles = useCallback(() => {
        const uploaderApi = uploaderRef.current?.getAPI();

        if (uploaderApi) {
            uploaderApi.initFlow();
        }
    }, []);

    if (isLoading || !uploadSignature) {
        return null;
    }

    return (
        <div className="flex min-w-0 grow flex-col gap-3 items-start self-stretch">
            {value.length > 0 && (
                <div className="flex min-w-0 w-full flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                    {value.map((file) => (
                        <div key={file.externalUuid} className="w-40 shrink-0">
                            <FileCard file={file} onRemove={handleRemove} />
                        </div>
                    ))}
                </div>
            )}
            <Button variant="link" icon={PlusIcon} onClick={handleAddFiles} className="p-2">
                {t('tasks__form__add_files')}
            </Button>
            <FileUploaderRegular
                apiRef={apiRefCallback}
                pubkey={String(UPLOADCARE_PUBLIC_KEY)}
                cdnCname={String(UPLOADCARE_CDN_NAME)}
                classNameUploader="uc-light"
                sourceList="local, camera"
                userAgentIntegration="llm-react"
                filesViewMode="list"
                localeName={locale}
                secureSignature={uploadSignature.signature}
                secureExpire={String(uploadSignature.expire)}
                metadata={uploadMetadata}
                onDoneClick={handleDoneClick}
                headless
            />
        </div>
    );
};
